export enum CampaignStatus {
    DRAFT = 'draft',
    PROCESSING = 'processing',
    IN_PROGRESS = 'in_progress',
    COMPLETED = 'completed',
    FAILED = 'failed',
    CANCELLED = 'cancelled',
}

export enum MessageStatus {
    PENDING = 'pending',
    QUEUED = 'queued',
    SENDING = 'sending',
    SENT = 'sent',
    DELIVERED = 'delivered',
    FAILED = 'failed',
    INVALID_NUMBER = 'invalid_number',
}

export interface Campaign {
    id: number;
    name: string;
    description?: string;
    message_template: string;
    sender_id: string;
    client_name?: string;
    client_email?: string;
    status: CampaignStatus;
    total_contacts: number;
    total_sent: number;
    total_delivered: number;
    total_failed: number;
    total_pending: number;
    currency: string;
    amount_per_sms: number;
    workmanship_fee: number;
    sender_id_price: number;
    estimated_revenue: number;
    actual_revenue: number;
    scheduled_at?: string;
    started_at?: string;
    completed_at?: string;
    upload_filename?: string;
    created_at: string;
    updated_at: string;
    success_rate: number;
    is_active: boolean;
}

export interface CampaignCreate {
    name: string;
    description?: string;
    message_template: string;
    sender_id: string;
    client_name?: string;
    client_email?: string;
    scheduled_at?: string;
    created_by?: string;
}

export interface CampaignUpdate {
    name?: string;
    description?: string;
    message_template?: string;
    sender_id?: string;
    client_name?: string;
    client_email?: string;
    scheduled_at?: string;
    status?: CampaignStatus;
}

export interface CampaignStats {
    total_campaigns: number;
    active_campaigns: number;
    completed_campaigns: number;
    total_messages_sent: number;
    total_messages_delivered: number;
    total_messages_failed: number;
    overall_success_rate: number;
    estimated_revenue: number;
    actual_revenue: number;
    currency: string;
}

export interface Contact {
    id: number;
    campaign_id: number;
    name: string;
    phone_number: string;
    email?: string;
    scheduled_time?: string[];
    custom_fields?: Record<string, any>;
    is_valid: boolean;
    validation_error?: string;
    created_at: string;
}

export interface ContactPayload {
    name?: string;
    phone_number: string;
    email?: string;
    scheduled_time?: string | string[];
    custom_fields?: Record<string, any>;
}

export interface ContactUpdatePayload {
    name?: string;
    phone_number?: string;
    email?: string;
    scheduled_time?: string | string[];
    custom_fields?: Record<string, any>;
}

export interface Message {
    id: number;
    campaign_id: number;
    contact_id: number;
    message_text: string;
    sender_id: string;
    status: MessageStatus;
    error_message?: string;
    retry_count: number;
    queued_at?: string;
    sent_at?: string;
    delivered_at?: string;
    failed_at?: string;
    created_at: string;
}

export interface PaginatedResponse<T> {
    items: T[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
}

export interface FileUploadResponse {
    filename: string;
    file_path: string;
    total_rows: number;
    valid_rows: number;
    invalid_rows: number;
    errors: Array<Record<string, any>>;
}

export interface CampaignExecuteResponse {
    campaign_id: number;
    status: string;
    message: string;
    task_id?: string;
    total_contacts: number;
    estimated_duration_minutes?: number;
}

export type UserRole = 'admin' | 'staff' | 'viewer';

export interface User {
    id: number;
    email: string;
    full_name: string;
    role: UserRole;
    is_active: boolean;
    last_login_at?: string;
    created_at: string;
}

export interface LoginResponse {
    access_token: string;
    token_type: string;
    user: User;
}

export interface SystemSettings {
    id: number;
    currency: string;
    amount_per_sms: number;
    workmanship_fee: number;
    sender_id_price: number;
    is_active: boolean;
    updated_by_user_id?: number;
    created_at: string;
    updated_at: string;
}

export interface AuditLog {
    id: number;
    user_id?: number;
    user_email?: string;
    action: string;
    resource_type?: string;
    resource_id?: string;
    method?: string;
    path?: string;
    ip_address?: string;
    user_agent?: string;
    status_code?: number;
    details?: Record<string, any>;
    created_at: string;
}

export interface RevenueReportRow {
    campaign_id: number;
    campaign_name: string;
    status: CampaignStatus;
    total_contacts: number;
    total_sent: number;
    total_delivered: number;
    total_failed: number;
    amount_per_sms: number;
    workmanship_fee: number;
    sender_id_price: number;
    estimated_revenue: number;
    actual_revenue: number;
    currency: string;
    created_at: string;
    completed_at?: string;
}

export interface RevenueReport {
    rows: RevenueReportRow[];
    totals: {
        campaigns: number;
        total_sent: number;
        total_delivered: number;
        estimated_revenue: number;
        actual_revenue: number;
        currency: string;
    };
}
